"use client";

import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faGoogle,
    faMicrosoft,
    faAmazon,
    faApple,
    faFacebook,
    faTwitter,
    faSpotify,
    faSlack,
    faDropbox,
    faGithub,
    faLinkedin,
    faStripe,
    faShopify,
    faPaypal,
    faUber,
    faAirbnb,
    faSalesforce,
    faFigma,
    faDiscord,
    faYoutube,
    faTwitch,
    faSnapchat,
    faPinterest,
    faReddit,
    IconDefinition,
} from "@fortawesome/free-brands-svg-icons";

// Brand icon mapping
const brandIcons: Record<string, IconDefinition> = {
    google: faGoogle,
    microsoft: faMicrosoft,
    amazon: faAmazon,
    apple: faApple,
    facebook: faFacebook,
    twitter: faTwitter,
    spotify: faSpotify,
    slack: faSlack,
    dropbox: faDropbox,
    github: faGithub,
    linkedin: faLinkedin,
    stripe: faStripe,
    shopify: faShopify,
    paypal: faPaypal,
    uber: faUber,
    airbnb: faAirbnb,
    salesforce: faSalesforce,
    figma: faFigma,
    discord: faDiscord,
    youtube: faYoutube,
    twitch: faTwitch,
    snapchat: faSnapchat,
    pinterest: faPinterest,
    reddit: faReddit,
};

// Default company lists for two rows
const defaultRow1 = [
    { name: "Google", icon: "google" },
    { name: "Microsoft", icon: "microsoft" },
    { name: "Amazon", icon: "amazon" },
    { name: "Apple", icon: "apple" },
    { name: "Spotify", icon: "spotify" },
    { name: "Slack", icon: "slack" },
    { name: "GitHub", icon: "github" },
    { name: "Figma", icon: "figma" },
    { name: "Stripe", icon: "stripe" },
    { name: "YouTube", icon: "youtube" },
];

const defaultRow2 = [
    { name: "Facebook", icon: "facebook" },
    { name: "LinkedIn", icon: "linkedin" },
    { name: "Dropbox", icon: "dropbox" },
    { name: "Shopify", icon: "shopify" },
    { name: "PayPal", icon: "paypal" },
    { name: "Uber", icon: "uber" },
    { name: "Airbnb", icon: "airbnb" },
    { name: "Discord", icon: "discord" },
    { name: "Twitter", icon: "twitter" },
    { name: "Reddit", icon: "reddit" },
];

interface CompanyItem {
    name: string;
    icon: string;
}

// Individual company logo item component
const CompanyLogo: React.FC<{ company: CompanyItem; colorClass: string }> = ({ company, colorClass }) => {
    const iconName = company.icon || "";
    const icon = brandIcons[iconName.toLowerCase()];
    const logoUrl = (company as any).logo;

    if (icon) {
        return (
            <div className="flex items-center gap-3 px-8 py-4 transition-all hover:scale-110 cursor-default group">
                <FontAwesomeIcon
                    icon={icon}
                    className={`w-8 h-8 ${colorClass} transition-all group-hover:opacity-100`}
                />
                <span className={`text-lg font-semibold ${colorClass} whitespace-nowrap transition-all group-hover:opacity-100`}>
                    {company.name}
                </span>
            </div>
        );
    }

    if (logoUrl) {
        // Render custom image logo with grayscale effect that clears on hover
        return (
            <div className="flex items-center gap-3 px-8 py-4 transition-all hover:scale-110 cursor-default group">
                <img 
                    src={logoUrl} 
                    alt={company.name} 
                    className="h-8 w-auto object-contain transition-all opacity-70 group-hover:opacity-100 grayscale group-hover:grayscale-0" 
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                    }}
                />
                <span className={`text-lg font-semibold ${colorClass} whitespace-nowrap transition-all group-hover:opacity-100`}>
                    {company.name}
                </span>
            </div>
        );
    }

    return (
        <div className={`flex items-center gap-3 px-6 py-3 ${colorClass}`}>
            <span className="text-lg font-bold opacity-60">{company.name}</span>
        </div>
    );
};

// Infinite scroll row component
const InfiniteScrollRow: React.FC<{
    companies: CompanyItem[];
    direction: "left" | "right";
    speed?: number;
    colorClass: string;
}> = ({ companies, direction, speed = 40, colorClass }) => {
    // Duplicate the array multiple times for seamless infinite scroll
    const duplicatedCompanies = [...companies, ...companies, ...companies, ...companies];

    const animationStyle = {
        animationDuration: `${speed}s`,
    };

    return (
        <div className="relative overflow-hidden w-full py-2">
            {/* Gradient overlays for fade effect */}
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

            <div
                className={`flex w-max whitespace-nowrap pause-on-hover ${direction === "left" ? "animate-scroll-left" : "animate-scroll-right"}`}
                style={animationStyle}
            >
                {duplicatedCompanies.map((company, idx) => (
                    <CompanyLogo key={`${company.name}-${idx}`} company={company} colorClass={colorClass} />
                ))}
            </div>
        </div>
    );
};

export default function CompaniesAnimated({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";
    const primaryColor = content.colorPalette?.primary || "#3B82F6";

    const allCompanies: CompanyItem[] = content.companies && content.companies.length > 0 
        ? content.companies 
        : [...defaultRow1, ...defaultRow2];

    const halfLength = Math.ceil(allCompanies.length / 2);
    const row1Companies = allCompanies.slice(0, halfLength);
    const row2Companies = allCompanies.slice(halfLength);

    // The speed is 0.25x (very slow), so we use 160 seconds (40 * 4) for full duration
    const scrollSpeed = 20; // seconds - roughly 0.75x speed (assuming 40s is 1x)

    return (
        <section
            className={`relative py-20 bg-white overflow-hidden ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full opacity-[0.03]"
                    style={{ background: `radial-gradient(ellipse, ${primaryColor} 0%, transparent 70%)` }}
                />
            </div>

            <div className="relative z-10">
                <div className="text-center mb-12 px-6">
                    <p className="text-sm font-bold uppercase tracking-[0.2em] text-gray-400 mb-4">
                        <EditableText
                            value={(content.subtitle?.trim() ? content.subtitle : null) || "Trusted Worldwide"}
                            onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                            as="span"
                        />
                    </p>
                    <h2 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight">
                        <EditableText
                            value={(content.title?.trim() ? content.title : null) || "Powering the world's best teams"}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            as="span"
                        />
                    </h2>
                </div>

                {/* Animated Rows */}
                <div className="space-y-4">
                    {/* Row 1 - Moving Left */}
                    <InfiniteScrollRow
                        companies={row1Companies}
                        direction="left"
                        speed={scrollSpeed}
                        colorClass="text-gray-400 opacity-70"
                    />

                    {/* Row 2 - Moving Right (opposite direction) */}
                    <InfiniteScrollRow
                        companies={row2Companies}
                        direction="right"
                        speed={scrollSpeed}
                        colorClass="text-gray-400 opacity-70"
                    />
                </div>

                {/* Optional Stats */}
                <div className="mt-16 flex justify-center gap-12 md:gap-20 px-6">
                    <div className="text-center">
                        <div className="text-3xl md:text-4xl font-black text-gray-900">
                            <EditableText
                                value={(content.stat1Value?.trim() ? content.stat1Value : null) || "500+"}
                                onChange={(val) => onContentChange?.({ ...content, stat1Value: val })}
                                as="span"
                            />
                        </div>
                        <div className="text-sm text-gray-500 font-medium mt-1">
                            <EditableText
                                value={(content.stat1Label?.trim() ? content.stat1Label : null) || "Companies"}
                                onChange={(val) => onContentChange?.({ ...content, stat1Label: val })}
                                as="span"
                            />
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-3xl md:text-4xl font-black text-gray-900">
                            <EditableText
                                value={(content.stat2Value?.trim() ? content.stat2Value : null) || "1M+"}
                                onChange={(val) => onContentChange?.({ ...content, stat2Value: val })}
                                as="span"
                            />
                        </div>
                        <div className="text-sm text-gray-500 font-medium mt-1">
                            <EditableText
                                value={(content.stat2Label?.trim() ? content.stat2Label : null) || "Users"}
                                onChange={(val) => onContentChange?.({ ...content, stat2Label: val })}
                                as="span"
                            />
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-3xl md:text-4xl font-black text-gray-900">
                            <EditableText
                                value={(content.stat3Value?.trim() ? content.stat3Value : null) || "99.9%"}
                                onChange={(val) => onContentChange?.({ ...content, stat3Value: val })}
                                as="span"
                            />
                        </div>
                        <div className="text-sm text-gray-500 font-medium mt-1">
                            <EditableText
                                value={(content.stat3Label?.trim() ? content.stat3Label : null) || "Uptime"}
                                onChange={(val) => onContentChange?.({ ...content, stat3Label: val })}
                                as="span"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS for marquee animation */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes scroll-left {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                @keyframes scroll-right {
                    0% { transform: translateX(-50%); }
                    100% { transform: translateX(0); }
                }
                .animate-scroll-left {
                    animation: scroll-left linear infinite;
                }
                .animate-scroll-right {
                    animation: scroll-right linear infinite;
                }
                .pause-on-hover:hover {
                    animation-play-state: paused;
                }
            `}} />
        </section>
    );
}
